import React, { FunctionComponent, useEffect, useState, useCallback, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import Jitsi from 'components/Jitsi/Jitsi';
import { broadCastStorage } from 'helpers/utils';
import { decodeMeetingLink, encodeMeetingLink } from 'utils/meetingLinkUtils';
import { ref, get } from 'firebase/database';
import { db } from 'utils/firebase-config';
import {
  endVideoCall,
  endVideoCallKeepalive,
  fetchVideoRoomFromFirebase,
} from 'store/services/firebase-broadcast';

interface IBroadCast {
  roomStatus: Function;
}

interface RoomData {
  pk: number;
  topic: string;
  host: {
    pk: number;
    name: string;
  };
  participants: any[];
}

const normalizeRoomFromFirebase = (roomRaw: any, roomId: number | string): RoomData => ({
  pk: roomRaw.pk ?? roomId,
  topic: roomRaw.topic,
  host: {
    pk: roomRaw.host?.pk,
    name: roomRaw.host?.name,
  },
  participants: roomRaw.participants || [],
});

const BroadCast: FunctionComponent<IBroadCast> = (props: IBroadCast) => {
  const { roomStatus } = props;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const history = useHistory();
  const location: any = useLocation();
  const params = useParams<{ encodedData?: string }>();
  const [broadCastItem, setBroadCastItem] = useState<RoomData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [closeTime, setCloseTime] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const roomInitializedRef = useRef(false);

  // Test function to verify meeting link utilities
  const testMeetingLink = () => {
    try {
      const testRoom = {
        pk: 123,
        topic: 'Test Room',
        host: { pk: 456 },
        participants: []
      };
      
      const encoded = encodeMeetingLink(testRoom);
      console.log('Test encode result:', encoded);
      
      const decoded = decodeMeetingLink(encoded);
      console.log('Test decode result:', decoded);
      
      alert('Meeting link test successful! Check console for details.');
    } catch (error: any) {
      console.error('Meeting link test failed:', error);
      alert('Meeting link test failed: ' + error.message);
    }
  };

  /**
   * Handles meeting link flow by decoding URL parameters
   */
  const handleMeetingLinkFlow = useCallback(async (encodedData: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Decode the meeting link data (minimal: id + timestamp)
      const linkData = decodeMeetingLink(encodedData);

      const roomId = linkData.id;

      const snap = await get(ref(db, `rooms/${roomId}`));
      if (!snap.exists()) {
        throw new Error('Room not found');
      }
      const roomPreview = snap.val();
      const isHost = roomPreview.host?.pk === user.pk;

      const roomRaw = await fetchVideoRoomFromFirebase(roomId, {
        requireCallActive: !isHost,
      });

      const room = normalizeRoomFromFirebase(roomRaw, roomId);

      // Set room data and clean URL
      roomInitializedRef.current = true;
      setBroadCastItem(room);
      history.replace('/call', { selectedBroadCast: room });
      console.log('Meeting link flow completed successfully');
    } catch (error: any) {
      console.error('Meeting link error:', error);
      setError(error.message || 'Unable to join this video');
      
      // For generic errors we can optionally redirect. When error is the finished-message, we have already returned.
    } finally {
      setIsLoading(false);
    }
  }, [history, user.pk]);

  /**
   * Initializes the component with room data
   */
  useEffect(() => {
    const initializeRoom = async () => {
      if (params.encodedData) {
        handleMeetingLinkFlow(params.encodedData);
        return;
      }

      const selected = location.state?.selectedBroadCast;
      if (selected?.pk) {
        setIsLoading(true);
        setError(null);
        const isStartingVideo = location.state?.isStartingVideo === true;
        const requireCallActive = location.state?.requireCallActive === true;

        try {
          const roomRaw = await fetchVideoRoomFromFirebase(selected.pk, {
            isStartingVideo,
            requireCallActive,
          });
          roomInitializedRef.current = true;
          setBroadCastItem(normalizeRoomFromFirebase(roomRaw, selected.pk));
        } catch (err: any) {
          console.error('Failed to load video room:', err);
          setError(err.message || 'Unable to join this video');

          if (isStartingVideo && user.pk === selected?.host?.pk) {
            try {
              await endVideoCall(selected.pk);
              roomStatus({ id: selected.pk, status: 'CHAT' });
            } catch (rollbackErr) {
              console.error('Failed to rollback video start:', rollbackErr);
            }
          }

          const finishedMessages = [
            'The video call is not available.',
            'The video call has been finished.',
          ];
          if (finishedMessages.includes(err.message)) {
            setTimeout(() => history.push('/conversations-nearby'), 3000);
          }
        } finally {
          setIsLoading(false);
        }
        return;
      }

      if (roomInitializedRef.current) {
        return;
      }

      console.log('No room data available, redirecting to conversations');
      history.push('/conversations-nearby');
    };

    initializeRoom();
  }, [location.state, params.encodedData, handleMeetingLinkFlow, history, roomStatus, user.pk]);

  const handleClose = async (options?: { navigate?: boolean }) => {
    const shouldNavigate = options?.navigate ?? true;

    broadCastStorage.clear();
    
    // Only update room status if user is the host
    if (user.pk === broadCastItem?.host?.pk && broadCastItem?.pk != null) {
      try {
        await endVideoCall(broadCastItem.pk);
      } catch (error) {
        console.error('Error updating Firebase:', error);
      }

      roomStatus({
        id: broadCastItem.pk,
        status: 'CHAT',
      });
    }
    
    // Navigate after updates only during explicit in-app close actions.
    if (shouldNavigate) {
      history.push('/conversations-nearby');
    }
  };

  useEffect(() => {
    const time = localStorage.getItem('start_time');
    if(time === null || time === '') {
      localStorage.setItem('start_time', JSON.stringify(+new Date()));
    }
  }, []);

  // Update elapsed time display in real-time
  useEffect(() => {
    if (!isClosing) return;
    
    const startTime = performance.now();
    const interval = setInterval(() => {
      const elapsed = Math.round(performance.now() - startTime);
      setElapsedTime(elapsed);
    }, 50); // Update every 50ms for smooth display
    
    return () => clearInterval(interval);
  }, [isClosing]);

  // Host tab/window close: keepalive PATCH so Firebase still gets CHAT / call_active:false
  // (async SDK updates are often cancelled during unload).
  useEffect(() => {
    const isHost =
      user?.pk != null &&
      broadCastItem?.host?.pk != null &&
      user.pk === broadCastItem.host.pk;
    const roomId = broadCastItem?.pk;
    if (!isHost || roomId == null) return undefined;

    let ended = false;
    const endCallForUnload = () => {
      if (ended) return;
      ended = true;
      endVideoCallKeepalive(roomId);
      try {
        broadCastStorage.clear();
      } catch {
        // ignore
      }
    };

    const handlePageHide = (e: PageTransitionEvent) => {
      // Skip bfcache freeze; still run on real tab close / navigate away.
      if (e.persisted) return;
      endCallForUnload();
    };

    const handleBeforeUnload = () => {
      endCallForUnload();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [broadCastItem?.pk, broadCastItem?.host?.pk, user?.pk]);

  // Loading state for meeting links
  if (isLoading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-3">Joining meeting...</p>
      </div>
    );
  }

  // Error state for meeting links
  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Unable to Join Meeting</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">Redirecting to conversations...</p>
        </div>
      </div>
    );
  }

  // No room data - show error for meeting links
  if (!broadCastItem || !broadCastItem.pk) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">No Room Data</h4>
          <p>Unable to load room information.</p>
          <p><strong>Debug Info:</strong></p>
          <ul style={{ textAlign: 'left' }}>
            <li>Params: {JSON.stringify(params)}</li>
            <li>Location State: {JSON.stringify(location.state)}</li>
            <li>Current Path: {window.location.pathname}</li>
          </ul>
          <div style={{ marginTop: '20px' }}>
            <button 
              className="btn btn-secondary me-2"
              onClick={testMeetingLink}
            >
              Test Meeting Link
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => history.push('/conversations-nearby')}
            >
              Browse Rooms
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Jitsi handleClose={handleClose} broadCast={broadCastItem} user={user} />
      
      {/* Closing modal with time display */}
      {isClosing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          color: 'white'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            padding: '40px 50px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
            border: '1px solid rgba(0, 174, 239, 0.2)',
            minWidth: '320px'
          }}>
            <div className="spinner-border text-primary mb-3" role="status" style={{
              width: '3rem',
              height: '3rem'
            }}>
              <span className="sr-only">Processing...</span>
            </div>
            <h4 style={{ 
              marginBottom: '15px',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              {closeTime > 0 ? '✅ Room Closed' : '🚪 Closing Room...'}
            </h4>
            
            {closeTime === 0 ? (
              <>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#aaa',
                  marginBottom: '20px'
                }}>
                  Executing cleanup process
                </p>
                <div style={{
                  backgroundColor: 'rgba(116, 25, 117, 0.1)',
                  padding: '15px 20px',
                  borderRadius: '8px',
                  border: '1px solid rgba(116, 25, 117, 0.3)'
                }}>
                  <p style={{
                    fontSize: '14px',
                    color: '#741975',
                    marginBottom: '5px',
                    fontWeight: '500'
                  }}>
                    Elapsed Time
                  </p>
                  <p style={{
                    fontSize: '32px',
                    color: '#741975',
                    fontWeight: 'bold',
                    margin: '0',
                    fontFamily: 'monospace'
                  }}>
                    {elapsedTime}ms
                  </p>
                </div>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#666',
                  marginTop: '15px',
                  marginBottom: '0'
                }}>
                  • Updating Firebase<br/>
                  • Updating backend<br/>
                  • Clearing storage
                </p>
              </>
            ) : (
              <div style={{
                backgroundColor: 'rgba(0, 200, 100, 0.1)',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid rgba(0, 200, 100, 0.3)'
              }}>
                <p style={{ 
                  fontSize: '16px', 
                  color: '#00c864',
                  marginBottom: '10px',
                  fontWeight: '600'
                }}>
                  Process Completed Successfully!
                </p>
                <p style={{ 
                  fontSize: '28px', 
                  color: '#00c864',
                  fontWeight: 'bold',
                  margin: '0',
                  fontFamily: 'monospace'
                }}>
                  {closeTime}ms
                </p>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#888',
                  marginTop: '10px',
                  marginBottom: '0'
                }}>
                  Check console for detailed logs
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BroadCast;
